import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Archive, ArchiveRestore, Plus, Save, Trash2 } from 'lucide-react';
import { STRATUM_KINDS, type StratumDTO, type StratumKind } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useStrata } from '../../api/hooks';
import { toast } from '../../components/Toasts';
import { queryKeys } from '../../api/queryKeys';

const KIND_LABEL: Record<StratumKind, string> = { text: 'Free text', vocabulary: 'Pick from a list', tags: 'Tags (names, keywords)' };

/** One option per line: "Label" or "value = Label". */
const parseOptions = (text: string) =>
  text.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const [a, b] = l.includes('=') ? l.split('=').map((x) => x.trim()) : [l.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''), l];
    return { value: a || 'option', label: b || a || 'Option' };
  });
const formatOptions = (o: StratumDTO['options']) => o.map((x) => `${x.value} = ${x.label}`).join('\n');

export function StrataAdmin({ embedded = false }: { embedded?: boolean }) {
  const q = useStrata({ counts: true });
  const qc = useQueryClient();
  const items = q.data?.items ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.strata() });

  async function call(path: string, method: 'POST' | 'PATCH' | 'DELETE', json?: unknown, ok?: string) {
    try {
      await api(path, { method, ...(json !== undefined ? { json } : {}) });
      await refresh();
      if (ok) toast(ok);
      return true;
    } catch (err) {
      toast(errorMessage(err), 'error');
      return false;
    }
  }

  const active = items.filter((s) => !s.archived);
  return (
    <div className={embedded ? 'strata-embedded' : 'page'} data-testid="strata-admin">
      {!embedded && <h2>Timecode layers</h2>}
      <p className="lede">Layers of timecoded metadata on every asset: content descriptions, shot types, people, locations and so on. People add segments with in and out points on an asset’s Log tab; text in searchable layers is found by library search, and matches open the asset at that moment.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 900 }}>
        {items.map((s) => (
          <LayerCard key={s.id} s={s} first={active[0]?.id === s.id} last={active.at(-1)?.id === s.id}
            onSave={(patch) => call(`/api/v1/strata/${s.id}`, 'PATCH', patch, `Saved ${s.label}`)}
            onMove={(d) => {
              const i = active.findIndex((x) => x.id === s.id);
              const other = active[i + d];
              if (!other) return;
              void call(`/api/v1/strata/${s.id}`, 'PATCH', { position: other.position }).then(() => call(`/api/v1/strata/${other.id}`, 'PATCH', { position: s.position }));
            }}
            onArchive={(archived) => call(`/api/v1/strata/${s.id}`, 'PATCH', { archived }, archived ? `Hid ${s.label}` : `Showing ${s.label} again`)}
            onDelete={() => {
              const msg = s.segments
                ? `Delete the layer “${s.label}” and its ${s.segments} segment${s.segments === 1 ? '' : 's'}?\n\nThis is destructive and cannot be undone. Hide the layer instead to keep the segments.`
                : `Delete the layer “${s.label}”?`;
              if (window.confirm(msg)) void call(`/api/v1/strata/${s.id}${s.segments ? '?purge=true' : ''}`, 'DELETE', undefined, `Deleted ${s.label}`);
            }} />
        ))}
        <NewLayer onCreate={(body) => call('/api/v1/strata', 'POST', body, `Added ${body.label}`)} />
      </div>
    </div>
  );
}

function LayerCard({ s, first, last, onSave, onMove, onArchive, onDelete }: {
  s: StratumDTO; first: boolean; last: boolean; onSave: (patch: Partial<StratumDTO>) => Promise<boolean>; onMove: (d: -1 | 1) => void;
  onArchive: (archived: boolean) => void; onDelete: () => void;
}) {
  const [label, setLabel] = useState(s.label);
  const [color, setColor] = useState(s.color);
  const [options, setOptions] = useState(formatOptions(s.options));
  const [searchable, setSearchable] = useState(s.searchable);
  const dirty = label !== s.label || color !== s.color || searchable !== s.searchable || (s.kind === 'vocabulary' && options !== formatOptions(s.options));
  return (
    <div className="panel-card" data-testid="layer-card" style={s.archived ? { opacity: 0.6 } : undefined}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 10, alignItems: 'center' }}>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} aria-label={`${s.label} colour`} style={{ width: 32, height: 28, border: 0, background: 'transparent' }} disabled={s.system} />
        <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Layer name" disabled={s.archived || s.system} />
        <span className="pill" title={s.system ? 'Written by PRISM (automated QC); read-only' : undefined}>{s.system ? 'System · read-only' : KIND_LABEL[s.kind]}</span>
        <span className="faint" style={{ fontSize: 12 }}>{s.archived ? 'Hidden · ' : ''}{s.segments ?? 0} segment{s.segments === 1 ? '' : 's'}</span>
      </div>
      {s.kind === 'vocabulary' && !s.archived && (
        <div className="field"><label>Options (one per line, “value = Label”)</label><textarea className="textarea mono" rows={Math.min(8, s.options.length + 1)} value={options} onChange={(e) => setOptions(e.target.value)} /></div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label className="toggle-row" style={{ alignItems: 'center' }}><input type="checkbox" checked={searchable} onChange={(e) => setSearchable(e.target.checked)} disabled={s.archived || s.system} /> Searchable</label>
        <span style={{ flex: 1 }} />
        {!s.archived && <>
          <button className="btn ghost sm icon" aria-label="Move up" disabled={first} onClick={() => onMove(-1)}><ArrowUp size={13} /></button>
          <button className="btn ghost sm icon" aria-label="Move down" disabled={last} onClick={() => onMove(1)}><ArrowDown size={13} /></button>
        </>}
        {!s.system && <button className="btn primary sm" disabled={!dirty} onClick={() => void onSave({ label, color, searchable, ...(s.kind === 'vocabulary' ? { options: parseOptions(options) } : {}) })}><Save size={13} /> Save</button>}
        <button className="btn ghost sm" onClick={() => onArchive(!s.archived)} title={s.archived ? 'Show on the Log tab and in search again' : 'Hide from the Log tab and search; segments are kept'}>
          {s.archived ? <><ArchiveRestore size={13} /> Show</> : <><Archive size={13} /> Hide</>}
        </button>
        {!s.system && <button className="btn ghost sm danger" onClick={onDelete} data-testid="delete-layer"><Trash2 size={13} /> Delete</button>}
      </div>
    </div>
  );
}

function NewLayer({ onCreate }: { onCreate: (body: { label: string; kind: StratumKind; color: string; options: StratumDTO['options'] }) => Promise<boolean> }) {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<StratumKind>('text');
  const [color, setColor] = useState('#22c55e');
  const [options, setOptions] = useState('');
  return (
    <div className="panel-card" data-testid="new-layer">
      <h3><Plus size={15} /> New layer</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 10, alignItems: 'end' }}>
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Colour" style={{ width: 32, height: 28, border: 0, background: 'transparent' }} />
        <div className="field"><label htmlFor="layer-name">Name</label><input id="layer-name" className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Location, Mood, Transcript notes" /></div>
        <div className="field"><label htmlFor="layer-kind">Values</label>
          <select id="layer-kind" className="select" value={kind} onChange={(e) => setKind(e.target.value as StratumKind)}>
            {STRATUM_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </div>
      </div>
      {kind === 'vocabulary' && <div className="field"><label>Options (one per line)</label><textarea className="textarea" rows={4} value={options} onChange={(e) => setOptions(e.target.value)} placeholder={'Calm\nTense\nJoyful'} /></div>}
      <button className="btn primary sm" style={{ alignSelf: 'flex-start' }} disabled={!label.trim()} data-testid="create-layer"
        onClick={() => void onCreate({ label: label.trim(), kind, color, options: kind === 'vocabulary' ? parseOptions(options) : [] }).then((ok) => { if (ok) { setLabel(''); setOptions(''); } })}>
        <Plus size={13} /> Add layer
      </button>
    </div>
  );
}
